import type { StripePrice } from 'generated/types';

type PlanContainer = { recurring: StripePrice; oneTime: StripePrice | null };

enum Currency {
  Cad = 'cad',
  Usd = 'usd',
}

enum Interval {
  Month = 'month',
  Year = 'year',
}

export class Plan {
  private readonly recurring: PlanContainer[] = [];

  public constructor(recurring: PlanContainer[]) {
    this.recurring = recurring;
  }

  /**
   * Check that the provided currency and interval are valid
   * @param currency A currency e.g. 'cad'
   * @param interval An interval e.g. 'month'
   * @returns `true` if the currency and interval are valid, `false` otherwise
   */
  private static validateCurrencyAndInterval(
    currency: string,
    interval: string
  ): boolean {
    return (
      Object.values<string>(Currency).includes(currency) &&
      Object.values<string>(Interval).includes(interval)
    );
  }

  public getSubscriptionCost(interval: string, currency: string): number {
    const valid = Plan.validateCurrencyAndInterval(currency, interval);
    const subscription = this.getSubscription(interval, currency);
    if (subscription?.recurring.unit_amount && valid) {
      return subscription.recurring.unit_amount / 100;
    }
    return 0;
  }

  public getOneTimeCost(interval: string, currency: string): number {
    const valid = Plan.validateCurrencyAndInterval(currency, interval);
    const subscription = this.getSubscription(interval, currency);
    if (subscription?.oneTime?.unit_amount && valid) {
      return subscription.oneTime.unit_amount / 100;
    }
    return 0;
  }

  public getSubscription(
    interval: string,
    currency: string
  ): PlanContainer | null {
    const valid = Plan.validateCurrencyAndInterval(currency, interval);
    const subscription = this.recurring.find(
      ({ recurring }) =>
        recurring.recurring?.interval === interval &&
        recurring.currency === currency
    );
    return valid && subscription ? subscription : null;
  }
}
